import type { ChangeEmailInput } from '@clockwise/clients/users';
import { ApiError } from '@clockwise/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { usersApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

// Sheet that changes a user's email — the login identity. The server updates
// `ba_user.email` alongside `users.email`, so this is a dedicated flow rather
// than a field on the edit form (where email is read-only).
const FormSchema = z.object({
  email: z.string().min(1, 'Required').email('Enter a valid email').max(320),
});
type FormValues = z.infer<typeof FormSchema>;

interface Props {
  userId: string;
  /** The user's current email — shown as context and used to reject a no-op. */
  currentEmail: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserChangeEmailSheet({ userId, currentEmail, open, onOpenChange }: Props) {
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { email: '' },
  });

  // Start blank each time the sheet opens so the current address isn't
  // accidentally re-submitted, and stale field errors from a prior attempt
  // don't linger.
  useEffect(() => {
    if (!open) return;
    form.reset({ email: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload: ChangeEmailInput = { email: values.email.trim().toLowerCase() };
      return usersApi.changeEmail(userId, payload);
    },
    onSuccess: () => {
      toast.success('Email updated');
      // The email shows on the detail page, the list, and every audit label —
      // invalidate all three plus the audit feed that now carries the change.
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        for (const fe of err.fields) {
          if (fe.path === 'email') form.setError('email', { message: fe.message });
        }
        toast.error('Please fix the highlighted field');
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  const onSubmit = form.handleSubmit((v) => {
    // Client-side no-op guard so an unchanged submit doesn't round-trip
    // (the server also returns the row unchanged in that case).
    if (v.email.trim().toLowerCase() === currentEmail.trim().toLowerCase()) {
      form.setError('email', { message: 'Enter a different email than the current one' });
      return;
    }
    mutation.mutate(v);
  });
  const errors = form.formState.errors;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Change email</SheetTitle>
          <SheetDescription>
            Currently <span className="font-medium">{currentEmail}</span>. This is the user's login
            identity — after the change they sign in with the new address (their current session
            stays valid).
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto px-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">New email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="off"
                aria-invalid={Boolean(errors.email?.message)}
                {...form.register('email')}
              />
              {errors.email?.message ? (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              ) : null}
            </div>
          </div>

          <SheetFooter className="flex-row justify-end border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Change email'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
